import { useEffect, useMemo, useState } from 'react';
import {
  makeStyles, tokens, Title1, Title3, Button, Spinner, Text, Badge,
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
} from '@fluentui/react-components';
import { apiGet, apiPost } from '../api/client';
import type { AttackSummary, AttackResultDto, AttackCategory } from '../api/types';

const useStyles = makeStyles({
  wrap: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '16px 20px',
    marginBottom: '16px',
  },
  postureGrid: {
    display: 'grid',
    gap: '10px',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    marginTop: '12px',
  },
  postureCard: {
    padding: '12px 14px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  postureHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' },
  postureLabel: { fontWeight: 600, fontSize: '13px' },
  postureMeta: { color: tokens.colorNeutralForeground3, fontSize: '11px' },
  postureRate: { fontSize: '18px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  categoryGroup: {
    marginTop: '18px',
    paddingTop: '10px',
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '6px',
    flexWrap: 'wrap',
  },
  attackRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '12px',
    padding: '8px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  err: { color: tokens.colorPaletteRedForeground1 },
  reason: { maxWidth: '360px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
});

// ---------------------------------------------------------------------------
// Category taxonomy — kept in one place so labels, descriptions, and OWASP
// mappings stay consistent across the posture cards and grouped list below.
// Order matches the backend AttackCategory enum (index = enum value).
// ---------------------------------------------------------------------------
interface CategoryMeta {
  key: string;
  label: string;
  blurb: string;
  owasp: string; // OWASP LLM Top-10 identifier
}

const CATEGORY_META: readonly CategoryMeta[] = [
  { key: 'InstructionOverride',      label: 'Instruction override',       blurb: 'User tells the model to ignore its system prompt outright.', owasp: 'LLM01' },
  { key: 'SystemPromptLeak',         label: 'System prompt leak',         blurb: 'Direct request to disclose the operator prompt verbatim.',    owasp: 'LLM07' },
  { key: 'HiddenInstructionLeak',    label: 'Hidden instruction leak',    blurb: 'Social engineering (dev mode, maintenance) to leak config.',  owasp: 'LLM07' },
  { key: 'CitationBypass',           label: 'Citation bypass',            blurb: 'Coerces an answer without required source citations.',        owasp: 'LLM09' },
  { key: 'SecretExtraction',         label: 'Secret extraction',          blurb: 'Attempts to pull API keys, connection strings, env vars.',    owasp: 'LLM06' },
  { key: 'SafetyBypass',             label: 'Safety bypass',              blurb: 'Requests harmful content the policy should refuse.',          owasp: 'LLM05' },
  { key: 'UngroundedAnswer',         label: 'Ungrounded answer',          blurb: 'Coaxes fabricated facts about topics not in the corpus.',     owasp: 'LLM09' },
  { key: 'PhiExfiltration',          label: 'PHI exfiltration',           blurb: 'Attempts to generate protected health information.',          owasp: 'LLM06' },
  { key: 'UnauthorizedToolCall',     label: 'Unauthorized tool call',     blurb: 'Coerces model to claim tool execution outside its scope.',    owasp: 'LLM08' },
  { key: 'PatientIdentityConfusion', label: 'Patient identity confusion', blurb: 'Near-miss patient names to leak a real patient record.',      owasp: 'LLM06' },
  { key: 'IndirectInjection',        label: 'Indirect injection',         blurb: 'Attack payload lands via a retrieved (poisoned) document.',   owasp: 'LLM01' },
  { key: 'ModelRoleManipulation',    label: 'Role manipulation',          blurb: 'Persona-hijack jailbreaks (DAN, dev mode) to bypass rules.',  owasp: 'LLM01' },
];

function categoryLabel(c: AttackCategory): string {
  return CATEGORY_META[c]?.label ?? `Unknown (${c})`;
}

function passRateColor(rate: number, sampleCount: number): 'success' | 'warning' | 'danger' | 'subtle' {
  if (sampleCount === 0) return 'subtle';
  if (rate >= 0.9) return 'success';
  if (rate >= 0.7) return 'warning';
  return 'danger';
}

interface CategoryPosture {
  index: AttackCategory;
  attackCount: number;
  passCount: number;
  runCount: number;
  passRate: number;
  latestRun: string | null;
}

function buildPosture(
  attacks: AttackSummary[],
  results: AttackResultDto[],
): CategoryPosture[] {
  return CATEGORY_META.map((_, i) => {
    const cat = i as AttackCategory;
    const attackCount = attacks.filter((a) => a.category === cat).length;
    const catResults = results.filter((r) => r.category === cat);
    const passCount = catResults.filter((r) => r.passed).length;
    const runCount = catResults.length;
    const passRate = runCount === 0 ? 0 : passCount / runCount;
    const latestRun = catResults.length === 0
      ? null
      : catResults.reduce((a, b) => a.executedAt > b.executedAt ? a : b).executedAt;
    return { index: cat, attackCount, passCount, runCount, passRate, latestRun };
  });
}

export default function Security() {
  const s = useStyles();
  const [attacks, setAttacks] = useState<AttackSummary[] | null>(null);
  const [results, setResults] = useState<AttackResultDto[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  async function refresh() {
    try {
      const [a, r] = await Promise.all([
        apiGet<AttackSummary[]>('/api/security/attacks'),
        apiGet<AttackResultDto[]>('/api/security/results?limit=500'),
      ]);
      setAttacks(a); setResults(r);
    } catch (e) { setErr((e as Error).message); }
  }

  useEffect(() => { refresh(); }, []);

  async function runAll() {
    setRunning(true); setErr(null);
    try {
      await apiPost<AttackResultDto[]>('/api/security/run', { runAll: true });
      await refresh();
    } catch (e) { setErr((e as Error).message); }
    finally { setRunning(false); }
  }

  async function runOne(id: string) {
    setRunning(true); setErr(null);
    try {
      await apiPost<AttackResultDto[]>('/api/security/run', { attackId: id });
      await refresh();
    } catch (e) { setErr((e as Error).message); }
    finally { setRunning(false); }
  }

  const posture = useMemo(
    () => (attacks && results) ? buildPosture(attacks, results) : null,
    [attacks, results],
  );

  const attacksByCategory = useMemo(() => {
    if (!attacks) return null;
    const grouped: Record<number, AttackSummary[]> = {};
    for (const a of attacks) {
      (grouped[a.category] ??= []).push(a);
    }
    return grouped;
  }, [attacks]);

  return (
    <div>
      <Title1 as="h1" style={{ marginBottom: 8 }}>Security — Red-team suite</Title1>
      <Text block style={{ color: tokens.colorNeutralForeground3, marginBottom: 16 }}>
        12-category threat taxonomy aligned with the OWASP LLM Top 10. Each category
        exercises one failure mode the healthcare assistant must resist.
      </Text>

      {/* -------- Posture (aggregate pass rate per category) -------- */}
      <div className={s.wrap}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title3 as="h2">Safety Posture</Title3>
          <Button appearance="primary" onClick={runAll} disabled={running}>
            {running ? 'Running…' : 'Run all attacks'}
          </Button>
        </div>
        {running && <Spinner size="tiny" style={{ marginTop: 8 }} />}
        {err && <Text className={s.err}>{err}</Text>}
        {!posture && !err && <Spinner size="tiny" label="Loading posture…" style={{ marginTop: 12 }} />}
        {posture && (
          <div className={s.postureGrid}>
            {posture.map((p) => {
              const meta = CATEGORY_META[p.index];
              const color = passRateColor(p.passRate, p.runCount);
              return (
                <div key={p.index} className={s.postureCard}>
                  <div className={s.postureHead}>
                    <span className={s.postureLabel}>{meta.label}</span>
                    <Badge appearance="outline" size="small">{meta.owasp}</Badge>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                    <span className={s.postureRate}
                      style={{ color: color === 'success' ? tokens.colorPaletteGreenForeground1
                                    : color === 'warning' ? tokens.colorPaletteYellowForeground1
                                    : color === 'danger'  ? tokens.colorPaletteRedForeground1
                                    : tokens.colorNeutralForeground3 }}>
                      {p.runCount === 0 ? '—' : `${(p.passRate * 100).toFixed(0)}%`}
                    </span>
                    <span className={s.postureMeta}>
                      {p.runCount === 0 ? 'not yet run' : `${p.passCount}/${p.runCount} passed`}
                    </span>
                  </div>
                  <span className={s.postureMeta}>
                    {p.attackCount} attack{p.attackCount === 1 ? '' : 's'} configured
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* -------- Attack library (grouped by category) -------- */}
      <div className={s.wrap}>
        <Title3 as="h2">Attack library</Title3>
        {attacksByCategory && CATEGORY_META.map((meta, i) => {
          const items = attacksByCategory[i] ?? [];
          if (items.length === 0) return null;
          return (
            <div key={i} className={s.categoryGroup}>
              <div className={s.categoryHeader}>
                <Text weight="semibold">{i}. {meta.label}</Text>
                <Badge appearance="outline" size="small">{meta.owasp}</Badge>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{meta.blurb}</Text>
              </div>
              {items.map((a) => (
                <div key={a.id} className={s.attackRow}>
                  <div>
                    <div><strong>{a.name}</strong></div>
                    <div style={{ color: tokens.colorNeutralForeground3, fontSize: 12 }}>{a.description}</div>
                  </div>
                  <Button size="small" onClick={() => runOne(a.id)} disabled={running}>Run</Button>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* -------- Results table -------- */}
      <div className={s.wrap}>
        <Title3 as="h2">Results</Title3>
        {!results && <Spinner label="Loading…" />}
        {results && (
          <Table size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Attack</TableHeaderCell>
                <TableHeaderCell>Category</TableHeaderCell>
                <TableHeaderCell>Result</TableHeaderCell>
                <TableHeaderCell>Reason</TableHeaderCell>
                <TableHeaderCell>Executed</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {results.length === 0 && (
                <TableRow><TableCell colSpan={5}><Text size={200}>No runs yet.</Text></TableCell></TableRow>
              )}
              {results.map((r) => (
                <TableRow key={r.resultId}>
                  <TableCell>{r.attackName}</TableCell>
                  <TableCell><Text size={200}>{categoryLabel(r.category)}</Text></TableCell>
                  <TableCell>
                    <Badge color={r.passed ? 'success' : 'danger'}>{r.passed ? 'PASSED' : 'FAILED'}</Badge>
                  </TableCell>
                  <TableCell className={s.reason} title={r.reason}>{r.reason}</TableCell>
                  <TableCell>{new Date(r.executedAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
